// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: moving an emoji to another server (emoji-plan.md section 11): the route, the servers it can go to,
// and every text, so Obyrus can reuse them word for word. Exclude from upstream sync.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import type {ExpressionMoveResponse} from '@fluxer/schema/src/domains/guild/GuildExpressionMoveSchemas';
import {msg} from '@lingui/core/macro';

export const MOVE_TO_SERVER_DESCRIPTOR = msg({
	message: 'Move to server',
	comment:
		'Accessible label and tooltip of the move button on an emoji in the community emoji settings, and the title of the server list it opens.',
});
export const MOVE_EMOJI_NAME_CLASH_HINT_DESCRIPTOR = msg({
	message: 'If the name is taken there, _2 is added.',
	comment: 'Hint under the title of the server list for moving an emoji. Keep "_2" as written.',
});
export const MOVE_EMOJI_NO_SERVERS_DESCRIPTOR = msg({
	message: 'No other server where you can manage emojis.',
	comment: 'Shown in the server list for moving an emoji when there is no server to choose.',
});
export const MOVED_TO_SERVER_DESCRIPTOR = msg({
	message: 'Moved to {server}',
	comment: 'Toast after moving an emoji. {server} is the community name.',
});
export const MOVE_EMOJI_FAILED_DESCRIPTOR = msg({
	message: "Couldn't move the emoji.",
	comment: 'Error toast when moving an emoji failed.',
});
export const MOVE_EMOJI_NO_SLOTS_DESCRIPTOR = msg({
	message: '{server} has no free emoji slots.',
	comment: 'Error toast when the community chosen for a move has no free emoji slot. {server} is its name.',
});

export async function moveEmoji(
	guildId: string,
	emojiId: string,
	targetGuildId: string,
): Promise<ExpressionMoveResponse> {
	return (
		await http.post<ExpressionMoveResponse>(`${Endpoints.GUILD_EMOJI(guildId, emojiId)}/move`, {
			body: {target_guild_id: targetGuildId},
		})
	).body;
}

/** The servers an emoji can move to: every other one where the user may manage expressions, by name. */
export function emojiMoveTargets(
	guilds: ReadonlyArray<{id: string; name: string}>,
	canManageExpressions: (guildId: string) => boolean,
	currentGuildId: string,
): Array<{id: string; name: string}> {
	return guilds
		.filter((guild) => guild.id !== currentGuildId && canManageExpressions(guild.id))
		.map((guild) => ({id: guild.id, name: guild.name}))
		.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The move rule, as the server's (emoji-plan.md Q6) and the Flutter app's: Manage Expressions in the emoji's server
 * and in at least one other server (emoji-plan.md section 14).
 */
export function canMoveEmoji(
	guildId: string,
	guilds: ReadonlyArray<{id: string; name: string}>,
	canManageExpressions: (guildId: string) => boolean,
): boolean {
	return canManageExpressions(guildId) && emojiMoveTargets(guilds, canManageExpressions, guildId).length > 0;
}
